import React, { useState, useCallback, useEffect, useContext } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { bindActionCreators } from 'redux';
import { RedirectFlow } from 'groverPaymentSDK';

import { OrderMode } from 'ggApp/types/graphql';
import { storeActive as activeStoreSelector } from 'ggApp/modules/store/selectors';
import FLOW_NAMES from 'ggApp/modules/auth/v3/constants/flowNames';
import {
    isIncompletePrivateAccount as isIncompletePrivateAccountSelector,
    isIncompleteBusinessAccount as isIncompleteBusinessAccountSelector,
    isLegacyBusinessAccount as isLegacyBusinessAccountSelector,
    isLoggedIn as isLoggedInSelector,
    b2bRedirection as b2bRedirectionSelector,
    isVerifiedPrivateAccount as isVerifiedPrivateAccountSelector,
    isPrivateAccountVerificationInProgress as isPrivateAccountVerificationInProgressSelector,
    authFlow as authFlowSelector,
} from 'ggApp/modules/auth/selectors';
import { AuthModal } from 'ggApp/modules/auth/v3/components';
import { locationQuery as locationQuerySelector } from 'ggApp/modules/router/selectors';
import { COMMON_QUERY_PARAM, MODAL_QUERY_ACTIVE } from 'ggApp/modules/router/constants';
import {
    pushParams,
    removeParams,
    redirectus as redirectusAction,
} from 'ggApp/modules/router/actions';
import { PureTranslator } from 'ggApp/modules/translator/components';
import CheckoutModal from 'ggApp/modules/newCheckout/components/CheckoutModal';

import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import PreventSelfCheckoutModal from 'ggApp/shared/modals/preventSelfCheckout';
import {
    blockManagedAccountCheckoutEnabled,
    checkoutCartOverlayCTA as checkoutCartOverlayCTASelector,
    useFeatureFlags,
    ENCLOSED_CHECKOUT,
} from 'ggApp/modules/featureFlag';
import { sendAnalyticsEvent } from '@devsbb/analytics-client';

import { CheckoutCta } from './styled';

export const NewCheckoutAuthGuard = ({
    activeStore,
    isLoggedIn,
    checkoutFlow,
    locationQuery,
    pushUrlParams,
    removeUrlParams,
    isIncompletePrivateAccount,
    isVerifiedPrivateAccount,
    isPrivateAccountVerificationInProgress,
    isIncompleteBusinessAccount,
    isLegacyBusinessAccount,
    b2bRedirection,
    isLegacyCheckout,
    redirectus,
    initialAuthFlow,
    isBlockManagedAccountCheckout,
    checkoutCartOverlayCTA,
}) => {
    const [authModal, setAuthModal] = useState(null);
    const [preventSelfCheckoutModal, setPreventSelfCheckoutModal] = useState(null);
    const [checkoutModal, setCheckoutModal] = useState(null);
    const [authModalFlow, setAuthModalFlow] = useState(initialAuthFlow);
    const { isFlagEnabled } = useFeatureFlags();
    const {
        send,
        orderMode,
        isOverlayActive,
        cart: { items = [] },
        cartLoaded,
        user,
        userDataFetched,
        orderNumber,
    } = useContext(OrderContext);

    const isAccountVerificationRequired =
        orderMode === OrderMode.MIX &&
        isLoggedIn &&
        (isIncompletePrivateAccount ||
            (!isVerifiedPrivateAccount && !isPrivateAccountVerificationInProgress));

    const handleCheckoutModalByUrl = useCallback(
        (modal) => {
            const shouldModalOpened =
                !isIncompleteBusinessAccount &&
                !isIncompletePrivateAccount &&
                !isLegacyBusinessAccount &&
                !isBlockManagedAccountCheckout &&
                !isAccountVerificationRequired &&
                modal &&
                locationQuery[COMMON_QUERY_PARAM.checkoutModal] === MODAL_QUERY_ACTIVE;

            if (shouldModalOpened) {
                modal.openModal();
                send('UPDATE_SHOULD_FETCH_USER_DATA');
            }
        },
        [
            send,
            locationQuery,
            isIncompleteBusinessAccount,
            isIncompletePrivateAccount,
            isLegacyBusinessAccount,
            isBlockManagedAccountCheckout,
            isAccountVerificationRequired,
        ],
    );

    const createModalRefCheckout = useCallback(
        (modal) => {
            setCheckoutModal(modal);
            handleCheckoutModalByUrl(modal);
        },
        [handleCheckoutModalByUrl],
    );

    const createModalRefAuth = useCallback((modal) => {
        setAuthModal(modal);
    }, []);

    const createModalRefPreventCheckout = useCallback((modal) => {
        setPreventSelfCheckoutModal(modal);
    }, []);

    const openAuthModal = useCallback(() => {
        if (isAccountVerificationRequired) {
            setAuthModalFlow(FLOW_NAMES.MISSING_VERIFICATION_PRIVATE);
        } else {
            setAuthModalFlow(initialAuthFlow);
        }
        if (authModal) {
            // eslint-disable-next-line no-unused-expressions
            authModal?.openModal();
        }
    }, [authModal, initialAuthFlow, isAccountVerificationRequired]);

    const openPreventSelfCheckoutModal = useCallback(() => {
        // eslint-disable-next-line no-unused-expressions
        preventSelfCheckoutModal?.openModal();
    }, [preventSelfCheckoutModal]);

    const openCheckoutModal = useCallback(() => {
        sendAnalyticsEvent('Cart Continue Clicked', { store_id: activeStore.store_id });

        if (isLegacyCheckout) {
            if (isIncompleteBusinessAccount || isLegacyBusinessAccount) {
                openAuthModal();
            } else if (isBlockManagedAccountCheckout) {
                openPreventSelfCheckoutModal();
            } else if (
                !window.location.href.includes('/deals') &&
                !window.location.href.includes('/trending') &&
                !window.location.href.includes('/products/')
            ) {
                if (isFlagEnabled(ENCLOSED_CHECKOUT)) {
                    redirectus(
                        `/${activeStore.code}-${activeStore.activeLanguage}/checkout/${orderNumber}`,
                        true,
                    );
                } else {
                    redirectus(
                        `/${activeStore.code}-${activeStore.activeLanguage}?cm=active`,
                        true,
                    );
                }
            }
            return;
        }

        send('UPDATE_SHOULD_FETCH_USER_DATA');

        if (isOverlayActive) {
            send('UPDATE_OVERLAY_STATE', { isOverlayActive: false });
        }

        const userAuthorized =
            isLoggedIn &&
            !isIncompleteBusinessAccount &&
            !isIncompletePrivateAccount &&
            !isLegacyBusinessAccount &&
            !isAccountVerificationRequired;

        if (!userAuthorized) {
            openAuthModal();
            return;
        }

        if (isBlockManagedAccountCheckout) {
            openPreventSelfCheckoutModal();
            return;
        }

        if (checkoutModal) {
            if (!userDataFetched) {
                send('SET_USER', { data: user });
            }

            if (
                !window.location.href.includes('/for-you') &&
                !window.location.href.includes('/deals') &&
                !window.location.href.includes('/trending') &&
                !window.location.href.includes('/products/')
            ) {
                if (isFlagEnabled(ENCLOSED_CHECKOUT)) {
                    redirectus(
                        `/${activeStore.code}-${activeStore.activeLanguage}/checkout/${orderNumber}`,
                        true,
                    );
                } else {
                    redirectus(
                        `/${activeStore.code}-${activeStore.activeLanguage}?cm=active`,
                        true,
                    );
                }
            } else {
                checkoutModal.openModal();
                pushUrlParams({ [COMMON_QUERY_PARAM.checkoutModal]: MODAL_QUERY_ACTIVE });
            }
        }
    }, [
        isLegacyCheckout,
        isBlockManagedAccountCheckout,
        activeStore,
        send,
        isOverlayActive,
        isLoggedIn,
        isIncompleteBusinessAccount,
        isIncompletePrivateAccount,
        isLegacyBusinessAccount,
        isAccountVerificationRequired,
        checkoutModal,
        openAuthModal,
        openPreventSelfCheckoutModal,
        redirectus,
        userDataFetched,
        pushUrlParams,
        user,
        orderNumber,
        isFlagEnabled,
    ]);

    const handleAuthSuccess = useCallback(() => {
        // TODO: Most likely this check is not required,
        // since it will happen in "openCheckoutModal" method,
        // and flow setting would happen in "openAuthModal"
        if (isIncompletePrivateAccount) {
            setAuthModalFlow(FLOW_NAMES.MISSING_INFO_PRIVATE);
        }
        openCheckoutModal();
    }, [isIncompletePrivateAccount, openCheckoutModal]);

    const onCloseCheckoutModal = useCallback(() => {
        removeUrlParams([COMMON_QUERY_PARAM.checkoutModal, ...Object.values(RedirectFlow)]);
    }, [removeUrlParams]);

    useEffect(() => {
        const isCheckoutOpened = checkoutModal && checkoutModal?.state?.isShown;
        const isB2bRedirection = b2bRedirection && b2bRedirection.shouldBeRedirectedToBusiness;
        const isEmptyLoadedCart = !items.length && cartLoaded;
        const isNoWaitingModal =
            locationQuery[COMMON_QUERY_PARAM.waitingModal] !== MODAL_QUERY_ACTIVE;

        if (isCheckoutOpened && (isB2bRedirection || (isEmptyLoadedCart && isNoWaitingModal))) {
            checkoutModal.closeModal();
        }
    }, [items, checkoutModal, cartLoaded, locationQuery, b2bRedirection]);

    const ctaKey = {
        control: 'NEW_CHECKOUT_CART_OVERLAY_CTA',
        variant_1: 'NEW_CHECKOUT_CART_OVERLAY_CTA_VARIANT_1',
        variant_2: 'NEW_CHECKOUT_CART_OVERLAY_CTA_VARIANT_2',
        variant_3: 'NEW_CHECKOUT_CART_OVERLAY_CTA_VARIANT_3',
    }[checkoutCartOverlayCTA?.variantKey ?? 'control'];

    return (
        <div data-testid="new-checkout-guard">
            <CheckoutCta
                fullWidth
                color="red"
                onClick={openCheckoutModal}
                isDisabled={orderMode === 'MIX' && items.length < 3}
            >
                <PureTranslator tk={ctaKey} />
            </CheckoutCta>

            <CheckoutModal
                flow={checkoutFlow}
                modalRef={createModalRefCheckout}
                onClose={onCloseCheckoutModal}
            />

            <PreventSelfCheckoutModal modalRef={createModalRefPreventCheckout} />

            <AuthModal
                modalRef={createModalRefAuth}
                flow={authModalFlow}
                onSuccess={handleAuthSuccess}
                options={{ hasSuccessSignupStep: false, isCheckout: true }}
            />
        </div>
    );
};

const enhance = compose(
    connect(
        createStructuredSelector({
            activeStore: activeStoreSelector,
            isLoggedIn: isLoggedInSelector,
            locationQuery: locationQuerySelector,
            isIncompletePrivateAccount: isIncompletePrivateAccountSelector,
            isIncompleteBusinessAccount: isIncompleteBusinessAccountSelector,
            isLegacyBusinessAccount: isLegacyBusinessAccountSelector,
            b2bRedirection: b2bRedirectionSelector,
            isVerifiedPrivateAccount: isVerifiedPrivateAccountSelector,
            isPrivateAccountVerificationInProgress: isPrivateAccountVerificationInProgressSelector,
            initialAuthFlow: authFlowSelector,
            isBlockManagedAccountCheckout: blockManagedAccountCheckoutEnabled,
            checkoutCartOverlayCTA: checkoutCartOverlayCTASelector,
        }),
        (dispatch) => ({
            pushUrlParams: bindActionCreators(pushParams, dispatch),
            removeUrlParams: bindActionCreators(removeParams, dispatch),
            redirectus: bindActionCreators(redirectusAction, dispatch),
        }),
    ),
);
export default enhance(NewCheckoutAuthGuard);
